"use client";

import { Share, Plus, X } from "lucide-react";

interface Props {
  onClose: () => void;
}

/**
 * Step-by-step instructions for adding Community Plan to the iOS home screen.
 * iOS Web Push works ONLY when the user launches the installed PWA.
 */
export function IosInstallGuide({ onClose }: Props) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="App auf iPhone installieren"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-card shadow-xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="absolute right-3 top-3 rounded-lg p-2 text-foreground-subtle hover:bg-muted/40"
        >
          <X className="size-4" />
        </button>
        <div className="space-y-5 p-6">
          <div>
            <h2 className="text-h5 font-semibold">App auf dem iPhone installieren</h2>
            <p className="mt-1 text-body-sm text-foreground-subtle">
              In nur drei Schritten — danach funktionieren Push-Benachrichtigungen.
            </p>
          </div>

          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-sm font-semibold text-accent">
                1
              </span>
              <div className="space-y-1">
                <p className="text-sm font-medium">In Safari öffnen</p>
                <p className="text-xs text-foreground-subtle">
                  Diese Seite muss in Safari geöffnet sein (nicht in Chrome / einer anderen App).
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-sm font-semibold text-accent">
                2
              </span>
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  Tippe auf <Share className="inline size-4" /> Teilen unten in der Symbolleiste
                </p>
                <p className="text-xs text-foreground-subtle">
                  Auf iPad steht der Teilen-Button oben rechts.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-sm font-semibold text-accent">
                3
              </span>
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  Wähle <Plus className="inline size-4" /> «Zum Home-Bildschirm»
                </p>
                <p className="text-xs text-foreground-subtle">
                  Bestätige mit «Hinzufügen». Öffne die App ab jetzt vom Home-Bildschirm,
                  damit Push funktioniert.
                </p>
              </div>
            </li>
          </ol>

          <p className="rounded-xl bg-muted/40 px-3 py-2 text-xs text-foreground-subtle">
            Voraussetzung: iOS 16.4 oder neuer.
          </p>

          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground hover:bg-accent/90"
          >
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
}
